import { createFileRoute } from '@tanstack/react-router'
import { motion } from 'framer-motion'
import {
  BookOpen,
  Library,
  Briefcase,
  BarChart3,
  SlidersHorizontal,
  Accessibility,
  CheckCircle2,
  Eye,
  Hand,
  Flag,
} from 'lucide-react'
import {
  Container,
  PageHero,
  SectionHeading,
  FeatureCard,
  CtaBand,
  fadeUp,
} from '@/components/umbrella/ui'
import ProductShowcase from '@/components/umbrella/ProductShowcase'

export const Route = createFileRoute('/platform')({
  component: PlatformPage,
})

const CAPABILITIES = [
  {
    Icon: BookOpen,
    title: 'Core learning',
    body: 'Short, scenario-based lessons that teach people to spot, avoid, and report scams. Written for real people and tested for clarity.',
  },
  {
    Icon: Library,
    title: 'Scam library',
    body: 'A searchable reference of common and emerging scams with real examples, kept current as tactics change.',
  },
  {
    Icon: Briefcase,
    title: 'Tools & resources',
    body: 'Guides, checklists, printables, and shareable assets that turn awareness into action.',
  },
  {
    Icon: BarChart3,
    title: 'Reporting & engagement insights',
    body: 'Understand reach and engagement through privacy-first reporting, with no tracking of individuals.',
  },
  {
    Icon: SlidersHorizontal,
    title: 'Flexible deployment',
    body: 'White-label ready, with customizable branding, content, reporting routes, and integrations.',
  },
  {
    Icon: Accessibility,
    title: 'Accessible by design',
    body: 'Built to work on any device, for every level of tech confidence, with no account required to learn.',
  },
]

const PRINCIPLES = [
  'No account required to access the education',
  'No selling or sharing of personal data',
  'Continuously reviewed against current threats',
  'Plain language, tested for readability',
  'Works on older devices and slower connections',
  'Grounded in FTC and FBI reporting data',
]

const OUTCOMES = [
  {
    Icon: Eye,
    title: 'They recognize the tactic',
    body: 'Scams run on pressure, urgency, and false authority. People learn to feel the manipulation instead of memorizing a list of scam names.',
  },
  {
    Icon: Hand,
    title: 'They pause before paying',
    body: 'The one habit that stops most losses: stop and verify before moving money, clicking a link, or sharing a code.',
  },
  {
    Icon: Flag,
    title: 'They know where to turn',
    body: 'Clear next steps for reporting a scam and getting help, routed to the right place for your audience.',
  },
]

const EASE = [
  'Open it and start in seconds',
  'Big, readable text and simple navigation',
  'Find any scam or answer in a tap or two',
  'Works on phones, tablets, and older computers',
]

function PlatformPage() {
  return (
    <div className="bg-white">
      <PageHero
        label="The platform"
        title="Everything people need to outsmart scammers,"
        highlight="in one place."
        blurb="Short lessons, a searchable scam library, practical tools, and clear reporting guidance, together in one simple platform. People learn to spot scams and know exactly what to do next, with no account required and no jargon to wade through."
        primary={{ label: 'Talk to our team', href: '#contact' }}
        secondary={{ label: 'See how it works', href: '/how-it-works' }}
      />

      <section className="bg-slate-50 py-20">
        <Container>
          <SectionHeading
            label="The outcome"
            title="People who can protect themselves"
            blurb="People come away calmer, sharper, and harder to fool, not fearful."
          />
          <div className="mt-12 grid gap-5 md:grid-cols-3">
            {OUTCOMES.map(o => (
              <FeatureCard key={o.title} {...o} />
            ))}
          </div>
        </Container>
      </section>

      <Container className="py-20">
        <SectionHeading
          label="What's inside"
          title="A complete scam-prevention toolkit"
          blurb="Every part works together, from the first lesson to reporting a scam, so no one is left wondering what to do next."
        />
        <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {CAPABILITIES.map(c => (
            <FeatureCard key={c.title} {...c} />
          ))}
        </div>
      </Container>

      <section className="bg-slate-50 py-20">
        <Container className="grid items-center gap-14 lg:grid-cols-2">
          <motion.div {...fadeUp}>
            <SectionHeading
              label="Ease of use"
              title="Built for people, not power users"
              align="left"
            />
            <p className="mt-4 text-lg leading-relaxed text-slate-600">
              We design for the person least comfortable with technology. When it
              works effortlessly for them, it works for everyone you serve.
            </p>
            <ul className="mt-8 space-y-3.5">
              {EASE.map(item => (
                <li key={item} className="flex items-center gap-3 text-[15px] font-medium text-slate-800">
                  <CheckCircle2 className="h-5 w-5 shrink-0 text-blue-600" />
                  {item}
                </li>
              ))}
            </ul>
          </motion.div>
          <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.1 }}>
            <ProductShowcase />
          </motion.div>
        </Container>
      </section>

      <Container className="py-20">
        <SectionHeading
          label="Built on principle"
          title="Trustworthy by design"
          blurb="The platform is built to respect the people who use it. That is what makes it credible to deploy in your name."
        />
        <div className="mx-auto mt-10 grid max-w-3xl gap-3.5 sm:grid-cols-2">
          {PRINCIPLES.map(p => (
            <div key={p} className="flex items-start gap-3 text-[15px] leading-snug text-slate-700">
              <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-blue-600" />
              {p}
            </div>
          ))}
        </div>
      </Container>

      <CtaBand />
    </div>
  )
}
